/**
 * Tiny inline language switcher. Persists choice via i18next-browser-languagedetector.
 *
 * The optional `dark` prop flips the colors so the switcher is readable
 * over a light hero background. The Header passes this based on whether
 * it's currently overlaying the light hero or the dark page below.
 */

import { useTranslation } from "react-i18next";

import { cn } from "@/lib/cn";
import { supportedLanguages, type SupportedLanguage } from "@/i18n/config";

const labels: Record<SupportedLanguage, string> = {
  no: "NO",
  en: "EN",
};

type LanguageSwitcherProps = {
  /** When true, render with dark text on the light hero background. */
  dark?: boolean;
};

export function LanguageSwitcher({ dark = false }: LanguageSwitcherProps) {
  const { i18n } = useTranslation();
  const current = (i18n.resolvedLanguage as SupportedLanguage) ?? "no";

  return (
    <div
      className="flex items-center gap-1 text-xs"
      role="group"
      aria-label="Language"
      translate="no"
    >
      {supportedLanguages.map((lang) => {
        const isActive = current === lang;
        return (
          <button
            key={lang}
            type="button"
            onClick={() => void i18n.changeLanguage(lang)}
            translate="no"
            className={cn(
              "rounded px-2 py-1 transition-colors",
              dark
                ? isActive
                  ? "bg-slate-200 text-slate-900"
                  : "text-slate-500 hover:text-slate-900"
                : isActive
                  ? "bg-slate-800 text-white"
                  : "text-slate-400 hover:text-slate-100",
            )}
          >
            {labels[lang]}
          </button>
        );
      })}
    </div>
  );
}
